import { useMemo } from 'react';
import { Form } from 'react-final-form';

import GlobalLoader from 'components/GlobalLoader';
import NavigatorContent from 'components/NavigatorContent';
import { InitialValues } from 'forms/types';
import useNavigatorTracking from 'hooks/useNavigatorTracking';
import Steps from 'hooks/useNavigatorTracking/steps';
import goToNextHealthStep from 'utils/goToNextHealthStep';

import { HEADING } from './constants';
import DepressionForm from './form';
import { DepressionFormValues } from './form/types';
import { useDepressionProfileQuery } from './graphql/DepressionProfile';
import { useUpdateDepressionProfileMutation } from './graphql/UpdateDepressionProfile';
import buildInitialValues from './utils/buildInitialValues';
import buildInput from './utils/buildInput';
import { getValidator } from './utils/validate';

const Depression = () => {
  const trackStepCompleted = useNavigatorTracking(Steps.Depression);
  const { data, loading } = useDepressionProfileQuery();
  const initialValues = useMemo(() => buildInitialValues(data), [data]);
  const dateOfBirth = data?.currentUser.dateOfBirth;
  const validate = useMemo(() => getValidator({ dateOfBirth }), [dateOfBirth]);

  const [updateDepressionProfile] = useUpdateDepressionProfileMutation({
    onCompleted(res) {
      trackStepCompleted();
      goToNextHealthStep(res.updateDepression?.nextStepPath);
    },
  });

  const onSubmit = async (values: DepressionFormValues) => {
    await updateDepressionProfile({
      variables: {
        input: buildInput(values),
      },
    });
  };

  if (loading) {
    return <GlobalLoader />;
  }

  return (
    <NavigatorContent heading={HEADING} title={Steps.Depression}>
      <Form<DepressionFormValues, InitialValues<DepressionFormValues>>
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
      >
        {(formProps) => <DepressionForm {...formProps} />}
      </Form>
    </NavigatorContent>
  );
};

export default Depression;
